// frontend/src/tts.js
// Utilities for Text-to-Speech across languages.

export function normalizeTtsText(text) {
  if (!text) return "";
  let cleaned = String(text);
  // Same rule as userFacingText.normalizeSeparators: do not break Hebrew גוף "שואל/ת".
  cleaned = cleaned
    .replace(/\s+\/\s+/g, ", ")
    .replace(/\s+\//g, ", ")
    .replace(/\/\s+/g, ", ");
  cleaned = cleaned.replace(/\s{2,}/g, " ").trim();
  return cleaned;
}

export function listVoices() {
  if (!("speechSynthesis" in window)) return [];
  return window.speechSynthesis.getVoices() || [];
}


export function ensureVoicesReady(timeoutMs = 1500) {
  return new Promise((resolve) => {
    if (!("speechSynthesis" in window)) {
      resolve([]);
      return;
    }
    const synth = window.speechSynthesis;
    const initial = synth.getVoices() || [];
    if (initial.length) {
      resolve(initial);
      return;
    }
    let done = false;
    const previous = synth.onvoiceschanged;
    const finish = () => {
      if (done) return;
      done = true;
      synth.onvoiceschanged = previous || null;
      resolve(synth.getVoices() || []);
    };
    const timer = setTimeout(finish, timeoutMs);
    synth.onvoiceschanged = () => {
      clearTimeout(timer);
      if (typeof previous === "function") previous();
      finish();
    };
  });
}

export function bestVoicesForLang(lang = "he") {
  const voices = listVoices();
  if (!voices.length) return [];

  const want = lang.toLowerCase().startsWith("he")
    ? ["he", "he-il", "hebrew", "israel"]
    : lang.toLowerCase().startsWith("es")
    ? ["es", "es-es", "spanish", "español", "mexico", "latin"]
    : ["en", "en-us", "en-gb", "english"];

  // דירוג פשוט: קידום לפי התאמת lang והשם
  const score = (v) => {
    let s = 0;
    const nm = (v.name || "").toLowerCase();
    const lg = (v.lang || "").toLowerCase();
    want.forEach(w => {
      if (lg.includes(w)) s += 3;
      if (nm.includes(w)) s += 2;
    });
    // קולים "Natural/Online" נחשבים איכותיים יותר בדפדפני Edge/Chrome
    if (nm.includes("natural") || nm.includes("online") || nm.includes("google")) s += 1;
    return s;
  };

  return voices
    .map(v => ({ v, s: score(v) }))
    .sort((a, b) => b.s - a.s)
    .map(x => x.v);
}

export function getSavedTtsPrefs(lang = "he") {
  const rate = Number(localStorage.getItem("tts.rate") || 0.9);    // איטי־נעים
  const pitch = Number(localStorage.getItem("tts.pitch") || 1.0);
  const savedName = localStorage.getItem(`tts.voice.${lang}`) || "";
  return { rate, pitch, savedName };
}

export function saveTtsPrefs({ lang, rate, pitch, voiceName }) {
  if (rate) localStorage.setItem("tts.rate", String(rate));
  if (pitch) localStorage.setItem("tts.pitch", String(pitch));
  if (lang && voiceName) localStorage.setItem(`tts.voice.${lang}`, voiceName);
}

export function pickVoice(lang = "he") {
  const { savedName } = getSavedTtsPrefs(lang);
  const voices = bestVoicesForLang(lang);
  if (!voices.length) return null;
  const exact = voices.find(v => v.name === savedName);
  return exact || voices[0];
}


export function speakTextOnce(text, { lang = "he", rate = 0.9, pitch = 1.0, voiceName = "", onStart, onEnd, onError } = {}) {
  if (!("speechSynthesis" in window)) {
    alert("המכשיר לא תומך בקריאה בקול (Speech Synthesis).");
    onError && onError();
    return;
  }
  const synth = window.speechSynthesis;
  try {
    synth.cancel();
    if (synth.paused) synth.resume();
  } catch {
    // ignore
  }

  ensureVoicesReady().then(() => {
    const u = new SpeechSynthesisUtterance(String(text || ""));
    u.lang = lang === "he" ? "he-IL" : lang.toLowerCase().startsWith("es") ? "es-ES" : "en-US";
    const voice = voiceName
      ? (listVoices().find((v) => v.name === voiceName) || pickVoice(lang))
      : pickVoice(lang);
    if (voice) u.voice = voice;
    u.rate = rate;
    u.pitch = pitch;
    u.onstart = () => onStart && onStart();
    u.onend = () => onEnd && onEnd();
    u.onerror = () => onError && onError();
    synth.speak(u);
  });
}

// דואג להקריא תור של מקטעים ברצף

export function speakQueue(parts, { lang = "he", rate = 0.9, pitch = 1.0, onDone } = {}) {
  if (!("speechSynthesis" in window)) {
    alert("המכשיר לא תומך בקריאה בקול (Speech Synthesis).");
    return;
  }
  const synth = window.speechSynthesis;
  try {
    synth.cancel();
    if (synth.paused) synth.resume();
  } catch {
    // ignore
  }

  ensureVoicesReady().then(() => {
    const voice = pickVoice(lang);
    if (!voice || !(voice.lang || "").toLowerCase().includes(lang.slice(0, 2))) {
      alert("לא נמצא קול תואם לשפה שנבחרה. יש להתקין תמיכת דיבור (Speech) לשפה זו במערכת.");
    }

    let idx = 0;
    const next = () => {
      if (idx >= parts.length) { onDone && onDone(); return; }
      const text = (parts[idx] || "").toString().trim();
      idx += 1;
      if (!text) { next(); return; }

      const u = new SpeechSynthesisUtterance(text);
      u.lang = lang === "he" ? "he-IL" : lang.toLowerCase().startsWith("es") ? "es-ES" : "en-US";
      if (voice) u.voice = voice;
      u.rate = rate;
      u.pitch = pitch;
      u.onend = () => next();
      u.onerror = () => next();
      synth.speak(u);
    };
    next();
  });
}

export function stopSpeaking() {
  if ("speechSynthesis" in window) window.speechSynthesis.cancel();
}
